import * as React from 'react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { toast } from 'sonner';
import SettingsCard from '../../components/SettingsCard';
import SearchField from '../../components/SearchField';
import { useDialogRef, dialogPanelProps } from '../../components/a11y';
import { Switch } from '@/src/shadComponents/ui/switch';
import useAuthenticatedApi from '@/src/Authentication/components/hooks/useAuthenticatedApi';

/*
 * Gallery users admin — the consumer accounts behind face recognition
 * and rollups. Classic's page had silent errors and no pagination;
 * this one toasts failures and pages at 50. Reference photos upload
 * directly (classic's crop step is a recorded deviation — the server
 * accepts full images).
 */

interface GalleryUser {
    id: number;
    firstName: string;
    lastName: string;
    email: string | null;
    phoneNumber: string | null;
    igHandle: string | null;
    slug?: string | null;
    facialRecognitionOptedIn: boolean;
    needsRollupCommunication: boolean;
}

const PAGE = 50;

const FIELD_LABEL =
    'mb-1.5 block text-[12px] font-semibold text-muted-foreground';

const EMPTY_DRAFT = {
    firstName: '',
    lastName: '',
    email: '',
    phoneNumber: '',
    igHandle: '',
    slug: '',
    facialRecognitionOptedIn: false,
};

export default function AdminGalleryUsersV2() {
    const authenticatedApi = useAuthenticatedApi();

    const [users, setUsers] = useState<GalleryUser[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const [search, setSearch] = useState('');
    const [limit, setLimit] = useState(PAGE);
    const [dialog, setDialog] = useState<
        { kind: 'create' } | { kind: 'edit'; user: GalleryUser } | null
    >(null);
    const [draft, setDraft] = useState(EMPTY_DRAFT);
    const [photos, setPhotos] = useState<any[]>([]);
    const [uploading, setUploading] = useState(false);
    const [busy, setBusy] = useState(false);
    const [pushingAll, setPushingAll] = useState(false);
    const [dialogError, setDialogError] = useState<string | null>(null);
    const fileRef = useRef<HTMLInputElement>(null);
    const dialogRef = useDialogRef(dialog != null, () => setDialog(null));

    const load = async () => {
        const data = await authenticatedApi
            .get('/gallery-users')
            .then((r) => r.data ?? [])
            .catch(() => []);
        setUsers(data);
        setIsLoading(false);
    };

    useEffect(() => {
        load();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const filtered = useMemo(() => {
        const q = search.trim().toLowerCase();
        if (!q) return users;
        return users.filter((u) =>
            `${u.firstName} ${u.lastName} ${u.email ?? ''} ${
                u.phoneNumber ?? ''
            } ${u.igHandle ?? ''}`
                .toLowerCase()
                .includes(q),
        );
    }, [users, search]);

    const openCreate = () => {
        setDraft(EMPTY_DRAFT);
        setDialogError(null);
        setDialog({ kind: 'create' });
    };

    const openEdit = async (user: GalleryUser) => {
        setDraft({
            firstName: user.firstName ?? '',
            lastName: user.lastName ?? '',
            email: user.email ?? '',
            phoneNumber: user.phoneNumber ?? '',
            igHandle: user.igHandle ?? '',
            slug: user.slug ?? '',
            facialRecognitionOptedIn: !!user.facialRecognitionOptedIn,
        });
        setPhotos([]);
        setDialogError(null);
        setDialog({ kind: 'edit', user });
        const refs = await authenticatedApi
            .get(`/gallery-users/${user.id}/reference-photos`)
            .then((r) => r.data ?? [])
            .catch(() => []);
        setPhotos(refs);
    };

    const submit = async () => {
        if (!dialog || busy) return;
        if (!draft.firstName.trim() || !draft.lastName.trim()) {
            setDialogError('First and last name are required.');
            return;
        }
        setBusy(true);
        setDialogError(null);
        const body: any = {
            firstName: draft.firstName.trim(),
            lastName: draft.lastName.trim(),
            email: draft.email.trim() || null,
            phoneNumber: draft.phoneNumber.trim() || null,
            igHandle: draft.igHandle.trim() || null,
            facialRecognitionOptedIn: draft.facialRecognitionOptedIn,
        };
        if (dialog.kind === 'edit') body.slug = draft.slug.trim() || null;
        const ok =
            dialog.kind === 'create'
                ? await authenticatedApi
                      .post('/gallery-users', body)
                      .then(() => true)
                      .catch(() => false)
                : await authenticatedApi
                      .put(`/gallery-users/${dialog.user.id}`, body)
                      .then(() => true)
                      .catch(() => false);
        setBusy(false);
        if (ok) {
            setDialog(null);
            toast.success(
                dialog.kind === 'create' ? 'User added' : 'Saved',
            );
            load();
        } else {
            setDialogError("Couldn't save — try again.");
        }
    };

    const remove = async (user: GalleryUser) => {
        if (
            !window.confirm(
                `Delete ${user.firstName} ${user.lastName}? Their reference photos go with them.`,
            )
        )
            return;
        const prev = users;
        setUsers((current) => current.filter((u) => u.id !== user.id));
        const ok = await authenticatedApi
            .delete(`/gallery-users/${user.id}`)
            .then(() => true)
            .catch(() => false);
        if (!ok) {
            setUsers(prev);
            toast.error("Couldn't delete the user.");
        }
    };

    const pushRollup = async (user: GalleryUser) => {
        const ok = await authenticatedApi
            .post(`/gallery-users/${user.id}/push-rollup`)
            .then(() => true)
            .catch(() => false);
        if (ok) {
            toast.success('Rollup pushed');
            load();
        } else {
            toast.error("Couldn't push the rollup.");
        }
    };

    const pushAll = async () => {
        if (pushingAll) return;
        setPushingAll(true);
        const ok = await authenticatedApi
            .post('/gallery-users/push-rollups')
            .then(() => true)
            .catch(() => false);
        setPushingAll(false);
        if (ok) {
            toast.success('Rollups pushed');
            load();
        } else {
            toast.error("Couldn't push rollups.");
        }
    };

    const uploadPhoto = async (file: File) => {
        if (dialog?.kind !== 'edit' || uploading) return;
        setUploading(true);
        const formData = new FormData();
        formData.append('photo', file);
        const ok = await authenticatedApi
            .post(
                `/gallery-users/${dialog.user.id}/reference-photo`,
                formData,
            )
            .then(() => true)
            .catch(() => false);
        setUploading(false);
        if (ok) {
            const refs = await authenticatedApi
                .get(`/gallery-users/${dialog.user.id}/reference-photos`)
                .then((r) => r.data ?? [])
                .catch(() => []);
            setPhotos(refs);
        } else {
            toast.error("Couldn't upload the photo.");
        }
    };

    const removePhoto = async (photo: any) => {
        const prev = photos;
        setPhotos((current) => current.filter((p) => p.id !== photo.id));
        const ok = await authenticatedApi
            .delete(`/gallery-users/reference-photo/${photo.id}`)
            .then(() => true)
            .catch(() => false);
        if (!ok) {
            setPhotos(prev);
            toast.error("Couldn't delete the photo.");
        }
    };

    if (isLoading) {
        return (
            <div className="mb-6 h-40 animate-pulse rounded-[16px] bg-secondary" />
        );
    }

    return (
        <>
            <SettingsCard
                title="Gallery users"
                desc="Consumer accounts behind face recognition and photo rollups."
                foot={
                    <>
                        <span className="flex-1 text-[11.5px] text-faint">
                            {users.length} user{users.length === 1 ? '' : 's'}
                        </span>
                        <button
                            type="button"
                            disabled={pushingAll}
                            onClick={pushAll}
                            className="cursor-pointer rounded-full border border-border bg-transparent px-3.5 py-2 font-[inherit] text-[12px] font-semibold text-foreground disabled:opacity-40"
                        >
                            {pushingAll ? 'Pushing…' : 'Push rollups'}
                        </button>
                        <button
                            type="button"
                            onClick={openCreate}
                            className="cursor-pointer rounded-full border-0 bg-foreground px-[18px] py-2 font-[inherit] text-[12.5px] font-semibold text-background"
                        >
                            Add user
                        </button>
                    </>
                }
            >
                {users.length > 8 && (
                    <SearchField
                        className="mb-2 w-[220px]"
                        placeholder="Search users…"
                        value={search}
                        onChange={(value) => {
                            setSearch(value);
                            setLimit(PAGE);
                        }}
                    />
                )}
                {filtered.length === 0 && (
                    <p className="py-2 text-[13px] text-muted-foreground">
                        {search.trim()
                            ? 'No users match.'
                            : 'No gallery users yet.'}
                    </p>
                )}
                {filtered.slice(0, limit).map((user) => (
                    <div
                        key={user.id}
                        className="group flex items-center gap-3 border-b border-line py-[9px] text-[12.5px] last:border-b-0"
                    >
                        <span className="w-[150px] flex-none truncate font-semibold">
                            {user.firstName} {user.lastName}
                        </span>
                        <span className="min-w-0 flex-1 truncate text-muted-foreground">
                            {user.email || user.phoneNumber || '—'}
                        </span>
                        {user.igHandle && (
                            <span className="hidden max-w-[110px] truncate font-mono text-[10px] text-faint sm:block">
                                @{user.igHandle.replace(/^@/, '')}
                            </span>
                        )}
                        {user.facialRecognitionOptedIn && (
                            <span className="rounded-full border border-brand-soft bg-brand-soft px-2 py-[2px] font-mono text-[9px] font-semibold tracking-[0.1em] text-brand-ink">
                                FACE
                            </span>
                        )}
                        <span className="flex gap-3 text-[12px] font-semibold opacity-0 transition-opacity focus-within:opacity-100 group-hover:opacity-100">
                            {user.needsRollupCommunication && (
                                <button
                                    type="button"
                                    onClick={() => pushRollup(user)}
                                    className="cursor-pointer border-0 bg-transparent p-0 font-[inherit] text-brand-ink"
                                >
                                    push
                                </button>
                            )}
                            <button
                                type="button"
                                onClick={() => openEdit(user)}
                                className="cursor-pointer border-0 bg-transparent p-0 font-[inherit] text-muted-foreground hover:text-foreground"
                            >
                                edit
                            </button>
                            <button
                                type="button"
                                title="Delete user"
                                onClick={() => remove(user)}
                                className="cursor-pointer border-0 bg-transparent p-0 font-[inherit] text-faint hover:text-attention"
                            >
                                ×
                            </button>
                        </span>
                    </div>
                ))}
                {filtered.length > limit && (
                    <div className="flex items-center justify-between pt-3">
                        <span className="text-[12px] text-faint">
                            {limit} of {filtered.length}
                        </span>
                        <button
                            type="button"
                            onClick={() => setLimit((l) => l + PAGE)}
                            className="ps-pill cursor-pointer px-4 font-[inherit] text-[12.5px] font-semibold text-foreground"
                        >
                            Show more
                        </button>
                    </div>
                )}
            </SettingsCard>

            {dialog && (
                <div
                    className="psv2 fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-6"
                    onClick={() => setDialog(null)}
                >
                    <div
                        ref={dialogRef}
                        {...dialogPanelProps(
                            dialog.kind === 'create'
                                ? 'Add gallery user'
                                : `Edit ${dialog.user.firstName} ${dialog.user.lastName}`,
                        )}
                        className="flex max-h-[86vh] w-full max-w-[440px] flex-col overflow-y-auto rounded-[15px] border border-border bg-background p-5 shadow-xl outline-none"
                        onClick={(e) => e.stopPropagation()}
                    >
                        <div className="ps-label mb-3.5">
                            {dialog.kind === 'create'
                                ? 'Add gallery user'
                                : `Edit ${dialog.user.firstName} ${dialog.user.lastName}`}
                        </div>
                        <div className="mb-3 grid grid-cols-2 gap-3">
                            <div>
                                <label className={FIELD_LABEL}>
                                    First name
                                </label>
                                <input
                                    autoFocus
                                    className="ps-input w-full text-[13px]"
                                    value={draft.firstName}
                                    onChange={(e) =>
                                        setDraft((d) => ({
                                            ...d,
                                            firstName: e.target.value,
                                        }))
                                    }
                                />
                            </div>
                            <div>
                                <label className={FIELD_LABEL}>
                                    Last name
                                </label>
                                <input
                                    className="ps-input w-full text-[13px]"
                                    value={draft.lastName}
                                    onChange={(e) =>
                                        setDraft((d) => ({
                                            ...d,
                                            lastName: e.target.value,
                                        }))
                                    }
                                />
                            </div>
                        </div>
                        <div className="mb-3 grid grid-cols-2 gap-3">
                            <div>
                                <label className={FIELD_LABEL}>Email</label>
                                <input
                                    type="email"
                                    className="ps-input w-full text-[13px]"
                                    value={draft.email}
                                    onChange={(e) =>
                                        setDraft((d) => ({
                                            ...d,
                                            email: e.target.value,
                                        }))
                                    }
                                />
                            </div>
                            <div>
                                <label className={FIELD_LABEL}>Phone</label>
                                <input
                                    className="ps-input w-full text-[13px]"
                                    value={draft.phoneNumber}
                                    onChange={(e) =>
                                        setDraft((d) => ({
                                            ...d,
                                            phoneNumber: e.target.value,
                                        }))
                                    }
                                />
                            </div>
                        </div>
                        <div className="mb-3 grid grid-cols-2 gap-3">
                            <div>
                                <label className={FIELD_LABEL}>
                                    Instagram
                                </label>
                                <input
                                    className="ps-input w-full text-[13px]"
                                    placeholder="handle"
                                    value={draft.igHandle}
                                    onChange={(e) =>
                                        setDraft((d) => ({
                                            ...d,
                                            igHandle: e.target.value,
                                        }))
                                    }
                                />
                            </div>
                            {dialog.kind === 'edit' && (
                                <div>
                                    <label className={FIELD_LABEL}>
                                        Slug
                                    </label>
                                    <input
                                        className="ps-input w-full font-mono text-[12px]"
                                        value={draft.slug}
                                        onChange={(e) =>
                                            setDraft((d) => ({
                                                ...d,
                                                slug: e.target.value,
                                            }))
                                        }
                                    />
                                </div>
                            )}
                        </div>
                        <label className="mb-3 flex cursor-pointer items-center justify-between gap-2 text-[12.5px] text-muted-foreground">
                            Face recognition opted in
                            <Switch
                                checked={draft.facialRecognitionOptedIn}
                                onCheckedChange={(value) =>
                                    setDraft((d) => ({
                                        ...d,
                                        facialRecognitionOptedIn: value,
                                    }))
                                }
                            />
                        </label>

                        {dialog.kind === 'edit' &&
                            draft.facialRecognitionOptedIn && (
                                <div className="mb-3">
                                    <div className="ps-label mb-1.5">
                                        Reference photos
                                    </div>
                                    <div className="flex flex-wrap gap-2">
                                        {photos.map((photo: any) => (
                                            <span
                                                key={photo.id}
                                                className="group/photo relative h-[56px] w-[56px] overflow-hidden rounded-[8px] border border-border bg-secondary"
                                            >
                                                <img
                                                    src={
                                                        photo.uri ??
                                                        photo.url ??
                                                        photo.photoUri
                                                    }
                                                    alt=""
                                                    className="h-full w-full object-cover"
                                                />
                                                <button
                                                    type="button"
                                                    title="Delete photo"
                                                    onClick={() =>
                                                        removePhoto(photo)
                                                    }
                                                    className="absolute inset-0 flex cursor-pointer items-center justify-center border-0 bg-black/50 font-[inherit] text-[13px] font-semibold text-white opacity-0 transition-opacity focus-visible:opacity-100 group-hover/photo:opacity-100"
                                                >
                                                    ×
                                                </button>
                                            </span>
                                        ))}
                                        <button
                                            type="button"
                                            disabled={uploading}
                                            onClick={() =>
                                                fileRef.current?.click()
                                            }
                                            className="flex h-[56px] w-[56px] cursor-pointer items-center justify-center rounded-[8px] border border-dashed border-border bg-transparent font-[inherit] text-[18px] text-faint hover:text-foreground disabled:opacity-40"
                                        >
                                            {uploading ? '…' : '+'}
                                        </button>
                                        <input
                                            ref={fileRef}
                                            type="file"
                                            accept="image/jpeg,image/png"
                                            className="hidden"
                                            onChange={(e) => {
                                                const file =
                                                    e.target.files?.[0];
                                                if (file)
                                                    uploadPhoto(file);
                                                e.target.value = '';
                                            }}
                                        />
                                    </div>
                                </div>
                            )}

                        <div className="flex items-center gap-3">
                            <button
                                type="button"
                                disabled={busy}
                                onClick={submit}
                                className="cursor-pointer rounded-full border-0 bg-brand px-5 py-2 font-[inherit] text-[13px] font-semibold text-brand-foreground disabled:opacity-50"
                            >
                                {busy
                                    ? 'Saving…'
                                    : dialog.kind === 'create'
                                    ? 'Add user'
                                    : 'Save'}
                            </button>
                            <button
                                type="button"
                                onClick={() => setDialog(null)}
                                className="cursor-pointer border-0 bg-transparent p-0 font-[inherit] text-[12.5px] font-semibold text-muted-foreground"
                            >
                                Cancel
                            </button>
                        </div>
                        {dialogError && (
                            <p className="mt-2.5 text-[12px] font-semibold text-attention">
                                {dialogError}
                            </p>
                        )}
                    </div>
                </div>
            )}
        </>
    );
}
